import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Result,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { TableProps, UploadFile } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CloudUploadOutlined,
  DownloadOutlined,
  InboxOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { studentsApi, type BulkImportResult } from '@/api/students';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { useAuthStore } from '@/store/auth';

const TEMPLATE = [
  'admissionNumber,firstName,lastName,gender,dateOfBirth,addressLine,fatherName,relationship,cnic,phone,occupation,className,sectionName,enrollmentDate',
  'TSS-001,Muhammad,Qasim,Male,2014-03-15,12 Lalazar Colony,Shahzad,Father,34603-6224099-9,0305-9305821,Engineer,Grade 3,A,2026-04-01',
  ',Ahmed,Khan,Male,,,Bilal Khan,Father,,0300-1234567,,KG,A,',
].join('\n');

export function StudentsImportPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const hasAuthority = useAuthStore((s) => s.hasAuthority);
  const isSuperAdmin = hasAuthority('ROLE_SUPER_ADMIN');

  const [file, setFile] = useState<UploadFile | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const submit = useMutation({
    mutationFn: (raw: File) => studentsApi.bulkImportCsv(raw),
    onSuccess: (res) => {
      setResult(res);
      const summary = `Imported ${res.created} of ${res.total}${res.failed ? ` · ${res.failed} failed` : ''}`;
      if (res.failed === 0) message.success(summary);
      else message.warning(summary);
    },
    onError: (e) => message.error(extractError(e)),
  });

  if (!isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Bulk import is restricted"
        subTitle="This tool is only available to a super administrator."
        extra={<Button onClick={() => navigate('/students')}>Back to students</Button>}
      />
    );
  }

  const onRun = () => {
    const raw = file?.originFileObj as File | undefined;
    if (!raw) {
      message.error('Choose a CSV file first');
      return;
    }
    setResult(null);
    submit.mutate(raw);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const outcomeCols: TableProps<BulkImportResult['outcomes'][number]>['columns'] = [
    { title: 'CSV row', dataIndex: 'rowIndex', width: 90, render: (i: number) => `#${i + 2}` },
    {
      title: 'Status',
      dataIndex: 'success',
      width: 110,
      render: (s: boolean) =>
        s ? (
          <Tag color="success" icon={<CheckCircleFilled />}>
            Created
          </Tag>
        ) : (
          <Tag color="error" icon={<CloseCircleFilled />}>
            Failed
          </Tag>
        ),
    },
    {
      title: 'Reg #',
      dataIndex: 'registrationNumber',
      width: 150,
      render: (rn: string | null) => rn ?? '—',
    },
    { title: 'Error', dataIndex: 'error', render: (e: string | null) => e ?? '—' },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Students"
        title="Bulk import"
        description="Upload a CSV of students with parent + class info. Each row is processed independently — partial failures are reported below."
        actions={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/students')}>
              Back
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              Download template
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={submit.isPending}
              onClick={onRun}
              disabled={!file}
            >
              Import
            </Button>
          </Space>
        }
      />

      <Card>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Columns (case-insensitive, order-flexible): <code>admissionNumber</code>,{' '}
          <code>firstName</code>, <code>lastName</code>, <code>gender</code>,{' '}
          <code>dateOfBirth</code> (YYYY-MM-DD), <code>addressLine</code>,{' '}
          <code>fatherName</code>, <code>relationship</code> (Father/Mother/Guardian),{' '}
          <code>cnic</code>, <code>phone</code>, <code>occupation</code>,{' '}
          <code>className</code> (Playgroup / Nursery / KG / Grade 1…Grade 10),{' '}
          <code>sectionName</code> (A/B/C, defaults to A), <code>enrollmentDate</code>.
          Empty cells are treated as missing. Students are enrolled into the current academic year.
        </Typography.Paragraph>

        <Upload.Dragger
          accept=".csv,text/csv"
          beforeUpload={() => false}
          maxCount={1}
          fileList={file ? [file] : []}
          onChange={({ fileList }) => {
            setFile(fileList[0] ?? null);
            setResult(null);
          }}
          onRemove={() => {
            setFile(null);
            setResult(null);
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag your CSV file here</p>
          <p className="ant-upload-hint">
            One row per student. UTF-8 recommended. Re-running with a file that contains an already-imported
            <code> admissionNumber</code> will fail those rows only — successes are preserved.
          </p>
        </Upload.Dragger>
      </Card>

      {result && (
        <Card
          title={
            <Space>
              <CloudUploadOutlined />
              <span>Result</span>
              <Tag color="processing">{result.total} rows</Tag>
              <Tag color="success">{result.created} created</Tag>
              {result.failed > 0 && <Tag color="error">{result.failed} failed</Tag>}
            </Space>
          }
        >
          {result.outcomes.length === 0 ? (
            <Empty description="No rows processed" />
          ) : (
            <>
              {result.failed > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Some rows could not be imported"
                  description="Fix the offending CSV rows and re-upload. Already-created rows are not duplicated."
                />
              )}
              <Table
                rowKey={(r) => r.rowIndex}
                size="middle"
                columns={outcomeCols}
                dataSource={result.outcomes}
                pagination={{ pageSize: 20, showSizeChanger: false }}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
}
